import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { evaluateFlag } from '../api/featureFlagsApi';
import { FeatureFlagName } from '../types';

// ─── Constants ───────────────────────────────────────────────────────────────

const FLAGS_TO_EVALUATE: FeatureFlagName[] = [
  FeatureFlagName.IMPORT_SOURCES,
  FeatureFlagName.MCP_TOOLING,
  FeatureFlagName.RECIPE_MACROS_DISPLAY,
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface FeatureFlagsContextValue {
  flags: Partial<Record<string, boolean>>;
  isLoading: boolean;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [flags, setFlags] = useState<Partial<Record<string, boolean>>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    const fetchFlags = async () => {
      const userId = user?.userProviderId ?? null;
      const key = crypto.randomUUID();
      const metadata: Record<string, unknown> | undefined = userId ? { userId } : undefined;

      const entries = await Promise.allSettled(
        FLAGS_TO_EVALUATE.map(async (flagName) => {
          const value = await evaluateFlag(flagName, key, metadata);
          return [flagName, value] as const;
        }),
      );

      const resolved: Partial<Record<string, boolean>> = {};
      for (const result of entries) {
        if (result.status === 'fulfilled') {
          const [name, value] = result.value;
          resolved[name] = value;
        }
        // on rejection: flag defaults to false (already absent from resolved)
      }

      setFlags(resolved);
      setIsLoading(false);
    };

    fetchFlags().catch(() => {
      // all flags default to false on complete failure
      setFlags({});
      setIsLoading(false);
    });

    // Re-evaluate when the tab becomes visible (e.g. user switches back)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchFlags().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, authLoading]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useFeatureFlagsContext(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlagsContext must be used inside FeatureFlagsProvider');
  }
  return ctx;
}
